import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import { ArrowRight, Factory, ShieldCheck, Globe, Truck, Cog, Users } from 'lucide-react';

/* Logo mapping */
const clientLogos = {
  'Maruti Suzuki': '/logos/maruti-suzuki.png',
  'Tata Motors': '/logos/tata-motors.png',
  'Mahindra': '/logos/mahindra.png',
  'JCB India': '/logos/jcb.png',
  'Volvo Buses': '/logos/volvo.png',
  'DAF Trucks': '/logos/daf.png',
  'Scania': '/logos/scania.png',
  'Navistar': '/logos/navistar.png',
  'Gabriel India': '/logos/gabriel-india.png',
  'Sandhar Technologies': '/logos/sandhar.png',
  'Krishna Grupo Antolin': '/logos/grupo-antolin.png',
  'ALP Nishikawa': '/logos/alp-nishikawa.png',
  'Anand Group': '/logos/anand-group.png',
  'Anand NVH': '/logos/anand-nvh.png',
  'SKH Metals': '/logos/skh-metals.png',
  'Bony Polymers': '/logos/bony-polymers.png',
  'Citizen Components': '/logos/citizen-components.png',
  'Imperial Auto Industries': '/logos/imperial-auto.png',
  'Imperial Martor': '/logos/imperial-martor.png',
  'KML Seatings': '/logos/kml-seatings.png',
  'Ideal Precision Components': '/logos/ideal-precision.png',
};

/* Client Data - Grouped by category */
const clientGroups = [
  {
    category: 'OEMs & Vehicle Manufacturers',
    icon: Truck,
    clients: ['Maruti Suzuki', 'Tata Motors', 'Mahindra', 'JCB India', 'Volvo Buses', 'DAF Trucks', 'Scania', 'Navistar'],
  },
  {
    category: 'Tier 1 & Major Suppliers',
    icon: ShieldCheck,
    clients: ['Gabriel India', 'Sandhar Technologies', 'Krishna Grupo Antolin', 'ALP Nishikawa', 'Anand Group', 'Anand NVH'],
  },
  {
    category: 'Industrial & Component Manufacturers',
    icon: Cog,
    clients: ['SKH Metals', 'Bony Polymers', 'Citizen Components', 'Imperial Auto Industries', 'Imperial Martor', 'KML Seatings', 'Ideal Precision Components'],
  },
];

const allClients = clientGroups.flatMap(g => g.clients);

const useCases = [
  'Quality Execution', 'Standards Control', 'Complaint & CAPA Management',
  'Supplier Governance', 'End-to-End Traceability', 'IoT Machine Visibility', 'Audit Evidence Readiness',
];

const industries = [
  'Automotive OEMs', 'Tier 1 Manufacturers', 'Tier 2 Suppliers', 'Multi-Plant Groups',
  'Precision Engineering', 'Sheet Metal & Stamping', 'Plastic Moulding', 'Forging & Casting',
  'Electronics Manufacturing', 'Electrical Components', 'Defence Manufacturing', 'Heavy Engineering',
];

const testimonialPlaceholders = [
  { role: 'Plant Head', company: 'Tier 1 Automotive Manufacturer', text: 'MUSHIN helped us digitize our IATF-critical workflows and significantly reduce our audit preparation time. Evidence retrieval that took days now takes minutes.' },
  { role: 'Quality Head', company: 'Tier 2 Component Maker', text: 'Our traceability went from paper-based to 100% digital with Mushin Factory OS. Customer complaints get traced back within minutes, not days.' },
  { role: 'SQE Manager', company: 'OEM Manufacturing Group', text: 'Mushin Vendor Connect gave us structured supplier quality governance. Audits, scorecards, and CAPA follow-up, all in one system.' },
];

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return <section ref={ref} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>{children}</section>;
}

export default function ClientsPage() {
  return (
    <div className="pt-16">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Our Clients</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-[#111827]" data-testid="clients-title">
            Trusted across OEM, Tier 1,<br />and Tier 2 ecosystems
          </h1>
          <p className="mt-6 text-lg text-[#374151] max-w-2xl">
            Leading automotive OEMs, Tier 1 and Tier 2 manufacturers, and multi-plant groups rely on <span className="font-bold text-[#111827] tracking-wide">MUSHIN</span> to digitize IATF 16949-aligned plant operations, supplier quality, and IoT shopfloor visibility.
          </p>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="py-8 border-y border-[#F0F0F0] bg-[#FAFAFA] overflow-hidden">
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#FAFAFA] to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#FAFAFA] to-transparent z-10" />
          <div className="flex items-center gap-6 trust-scroll">
            {[...Array(2)].flatMap((_, rep) =>
              allClients.map((name, i) => (
                <div key={`${rep}-${i}`} className="flex-shrink-0 h-14 px-5 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center shadow-sm">
                  <img src={clientLogos[name]} alt={name} className="h-8 max-w-[120px] object-contain grayscale hover:grayscale-0 transition-all" loading="lazy" />
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Client Grid - Grouped */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Client Partners</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-4">Used by manufacturers across major automotive clusters</h2>
          <p className="text-[#374151] max-w-2xl mb-12">From global OEMs to precision component makers, <span className="font-bold text-[#111827] tracking-wide">MUSHIN</span> serves manufacturers who demand structured, audit-ready plant control.</p>

          <div className="space-y-12">
            {clientGroups.map((group, gi) => (
              <div key={gi} data-testid={`client-group-${gi}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 rounded-lg bg-[#FEF2F2] border border-[#FECACA] flex items-center justify-center">
                    <group.icon className="w-4 h-4 text-[#C41E3A]" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-bold text-[#111827] uppercase tracking-wider">{group.category}</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                  {group.clients.map((client, ci) => (
                    <div key={ci} className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-xl h-24 flex items-center justify-center px-5 hover:border-[#C41E3A]/30 hover:bg-white transition-all group" data-testid={`client-logo-${gi}-${ci}`}>
                      <img src={clientLogos[client]} alt={client} className="h-10 max-w-[140px] object-contain grayscale group-hover:grayscale-0 transition-all" loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Testimonials */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">What Clients Say</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-12">Plant-floor impact</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonialPlaceholders.map((t) => (
              <div key={t.role} className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-sm" data-testid={`testimonial-${t.role}`}>
                <p className="text-sm text-[#374151] leading-relaxed italic mb-5">"{t.text}"</p>
                <div className="border-t border-[#F0F0F0] pt-4">
                  <p className="text-[15px] font-semibold text-[#111827]">{t.role}</p>
                  <p className="text-[13px] text-[#4B5563]">{t.company}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Industries */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Industries We Serve</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-12">Across automotive and manufacturing verticals</h2>
          <div className="flex flex-wrap gap-3">
            {industries.map((ind, i) => (
              <div key={i} className="px-4 py-2.5 bg-[#FAFAFA] border border-[#E5E7EB] rounded-full text-sm text-[#374151] hover:border-[#C41E3A]/30 transition-colors">{ind}</div>
            ))}
          </div>
        </div>
      </Section>

      {/* Use Cases */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[13px] font-mono uppercase tracking-[0.25em] text-[#C41E3A] mb-4 font-medium">Use Cases</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#111827] mb-12">How automotive clients use <span className="tracking-wide">MUSHIN</span></h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCases.map((uc, i) => (
              <div key={i} className="bg-white border border-[#E5E7EB] rounded-xl p-6 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/[0.04] transition-all duration-300" data-testid={`usecase-card-${i}`}>
                <h3 className="font-heading text-base font-semibold text-[#111827] mb-2"><span className="tracking-wide">MUSHIN</span> for {uc}</h3>
                <p className="text-[15px] text-[#374151]">Deployed across IATF-aligned automotive plants of varying scale and complexity</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Multi-plant */}
      <Section className="py-24 border-t border-[#F0F0F0] bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#FAFAFA] border border-[#E5E7EB] rounded-2xl p-8 lg:p-12 shadow-sm">
            <h2 className="font-heading text-2xl font-bold text-[#111827] mb-4">Multi-plant automotive rollout readiness</h2>
            <p className="text-[#374151] leading-relaxed mb-4"><span className="font-bold text-[#111827] tracking-wide">MUSHIN</span> is designed for automotive enterprises that need to standardize IATF / VDA 6.3-compliant operations across multiple plants. Our platform supports centralized governance, plant-level customization, and leadership-level visibility across locations.</p>
            <p className="text-[#374151] leading-relaxed">Whether you are rolling out to a single department at a Tier 2 plant or deploying across an entire OEM manufacturing group, <span className="font-bold text-[#111827] tracking-wide">MUSHIN</span> provides the structure, scalability, and deployment support.</p>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-24 text-center border-t border-[#F0F0F0] bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="font-heading text-3xl font-bold text-[#111827]">Join leading automotive manufacturers</h2>
          <p className="mt-4 text-[#374151]">See how <span className="font-bold text-[#111827] tracking-wide">MUSHIN</span> can help your automotive plant.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="bg-[#C41E3A] hover:bg-[#A11830] text-white px-7 py-3 rounded-full text-[15px] font-medium transition-colors inline-flex items-center gap-2" data-testid="clients-demo-cta">
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border border-[#D1D5DB] hover:border-[#9CA3AF] text-[#374151] px-7 py-3 rounded-full text-[15px] font-medium transition-colors" data-testid="clients-quote-cta">Get a Quote</a>
          </div>
        </div>
      </section>
    </div>
  );
}
